import { webStorage } from "@/lib/web-storage"

const STORAGE_KEY = "mindtrip.lastChildAges" as const

export const DEFAULT_CHILD_AGE = 3

const MIN_CHILD_AGE = 0
const MAX_CHILD_AGE = 17

function isValidChildAge(age: unknown): age is number {
  return (
    typeof age === "number" &&
    Number.isInteger(age) &&
    age >= MIN_CHILD_AGE &&
    age <= MAX_CHILD_AGE
  )
}

function parseRememberedAges(value: unknown): number[] {
  if (!Array.isArray(value)) return []
  return value.filter(isValidChildAge)
}

export function getRememberedChildAges(): number[] {
  const stored = webStorage.tryGetJSON<unknown>(STORAGE_KEY, {
    defaultValue: [],
  })
  return parseRememberedAges(stored)
}

export function rememberChildAges(ages: number[]): void {
  if (ages.length === 0) return

  const remembered = getRememberedChildAges()
  const merged = [...remembered]

  ages.forEach((age, index) => {
    if (isValidChildAge(age)) {
      merged[index] = age
    }
  })

  webStorage.setJSON(STORAGE_KEY, merged)
}

export function parseChildAgesParam(
  value: string | undefined,
  childCount: number
): number[] | undefined {
  if (!value) return undefined

  const values = value.split(",")
  if (values.some((age) => !/^\d+$/.test(age))) return undefined

  const ages = values.map(Number)
  if (ages.length !== childCount || ages.some((age) => !isValidChildAge(age))) {
    return undefined
  }

  return ages
}

export function defaultChildAges(count: number, existing?: number[]): number[] {
  const remembered = getRememberedChildAges()
  const childAges: number[] = []

  for (let index = 0; index < count; index++) {
    const existingAge = existing?.[index]
    if (existingAge !== undefined && isValidChildAge(existingAge)) {
      childAges.push(existingAge)
      continue
    }

    const rememberedAge = remembered[index]
    if (rememberedAge !== undefined) {
      childAges.push(rememberedAge)
      continue
    }

    childAges.push(DEFAULT_CHILD_AGE)
  }

  return childAges
}
